import { useMemo } from 'react'
import Select from 'react-select'
import type { ProjectId } from '#shared/types'
import type { Project } from '#shared/projects'

interface Option {
  value: ProjectId
  label: string
}

interface Props {
  /** Paired with the field's `<label for>`, so the caption is clickable. */
  id: string
  /** The projects that may be logged against — archived ones filtered out. */
  projects: Project[]
  value: ProjectId | ''
  onChange: (id: ProjectId) => void
}

/**
 * The project picker: a dropdown you can type into.
 *
 * A plain `<select>` is fine at seven projects and poor at seventy, and the
 * list is stored data a manager keeps adding to. Typing three letters beats
 * scrolling a list whose length nobody controls.
 *
 * `react-select` in `unstyled` mode: it brings the combobox behaviour and
 * the ARIA wiring, and none of its own appearance. The look comes from the
 * same tokens every other field uses, so it is themed in light and dark by
 * the stylesheet rather than by a second palette written in JavaScript.
 */
export function ProjectSelect({ id, projects, value, onChange }: Props) {
  const options = useMemo<Option[]>(
    () => projects.map((project) => ({ value: project.id, label: project.name })),
    [projects],
  )

  // null, not undefined: undefined makes the component uncontrolled, and
  // clearing the project would then leave the old label on screen.
  const selected = options.find((option) => option.value === value) ?? null

  return (
    <Select<Option>
      inputId={id}
      options={options}
      value={selected}
      onChange={(option) => option && onChange(option.value)}
      placeholder="Choose a project…"
      noOptionsMessage={() => 'No project matches that.'}
      // react-select answers this with a hidden `required` input laid over
      // the control, so an empty project gets the browser's own message
      // pointing at the right field — which the form's own guard, a silent
      // early return in the submit handler, cannot do.
      required
      unstyled
      classNamePrefix="rs"
      className="rs"
      // Near the foot of a dialog there is nowhere below to open.
      menuPlacement="auto"
    />
  )
}
